import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

// Closed scroll-area primitive. Always-on scrollbar (Radix `type="always"`),
// always expands to fill parent (combines `tw-h-full` + flex grow so it
// works in both fixed-height and flex-col parents), and always reserves
// a right-side gutter for the scrollbar overlay so content never sits
// under the thumb.
//
// Caller passes children only — no axes today. If a horizontal-scroll or
// content-sized variant ever lands, lift the constraint from a real
// callsite at that point.
//
// `viewportRef` (optional) is forwarded to the inner Radix Viewport.
// Virtualization libraries (`@tanstack/react-virtual`) need a direct
// reference to the actual scrolling DOM node — Radix nests the
// scrollable Viewport one level below the Root. Consumers that don't
// virtualize leave `viewportRef` undefined; nothing changes for them.

const ScrollArea = React.forwardRef(({ children, className: _className, style: _style, viewportRef }, ref) => (
    <ScrollAreaPrimitive.Root
        ref={ref}
        className="tw-relative tw-h-full tw-min-h-0 tw-flex-1 tw-overflow-hidden tw-pr-2"
        type="always"
    >
        <ScrollAreaPrimitive.Viewport ref={viewportRef} className="tw-h-full tw-w-full tw-rounded-[inherit]">
            {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollAreaPrimitive.Scrollbar
            className="tw-flex tw-h-full tw-w-2.5 tw-touch-none tw-select-none tw-border-l tw-border-l-transparent tw-p-[1px] tw-transition-colors"
            orientation="vertical"
        >
            <ScrollAreaPrimitive.Thumb className="tw-relative tw-flex-1 tw-rounded-full tw-bg-border" />
        </ScrollAreaPrimitive.Scrollbar>
        <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
));

ScrollArea.displayName = 'ScrollArea';

export { ScrollArea };
